import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CheckCircle2, Truck, Recycle, Scale, Phone } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1763926025680-7966e45e48f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHw0fHxpbmR1c3RyaWFsJTIwbWV0YWwlMjB3YXJlaG91c2V8ZW58MHx8fHwxNzc3MTg2NzM1fDA&ixlib=rb-4.1.0&q=85";

const SERVICE_IMGS = {
  industrial:
    "https://images.unsplash.com/photo-1776254575132-fbc8f3d50210?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwc2NyYXAlMjBtZXRhbCUyMG1hY2hpbmVyeXxlbnwwfHx8fDE3NzcxODY3MjB8MA&ixlib=rb-4.1.0&q=85",
  machinery:
    "https://images.unsplash.com/photo-1767989394831-1ca05fe82dc6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxydXN0eSUyMGdlYXJzJTIwbWFjaGluZXJ5fGVufDB8fHx8MTc3NzE4NjczNHww&ixlib=rb-4.1.0&q=85",
  electrical:
    "https://images.pexels.com/photos/28286031/pexels-photo-28286031.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

export default function Home({ settings }) {
  const { t } = useI18n();
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    api.get("/gallery").then((r) => setGallery(r.data.slice(0, 6))).catch(() => {});
  }, []);

  const services = [
    { key: "industrial", title: t("services.industrial.title"), desc: t("services.industrial.desc"), img: SERVICE_IMGS.industrial },
    { key: "machinery", title: t("services.machinery.title"), desc: t("services.machinery.desc"), img: SERVICE_IMGS.machinery },
    { key: "electrical", title: t("services.electrical.title"), desc: t("services.electrical.desc"), img: SERVICE_IMGS.electrical },
  ];

  const benefits = [
    { icon: Scale, title: t("why.b1"), desc: t("why.b1d") },
    { icon: Truck, title: t("why.b2"), desc: t("why.b2d") },
    { icon: CheckCircle2, title: t("why.b3"), desc: t("why.b3d") },
    { icon: Recycle, title: t("why.b4"), desc: t("why.b4d") },
  ];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden grain" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="warehouse" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/75 to-[#0A0B0E]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-28 lg:pt-32 lg:pb-40">
          <div className="overline fade-up">{t("hero.overline")}</div>
          <h1
            className="font-display font-black uppercase tracking-tighter leading-[0.9] mt-6 text-white text-5xl sm:text-6xl lg:text-7xl xl:text-[110px] fade-up"
            style={{ animationDelay: "0.1s" }}
            data-testid="hero-title"
          >
            {t("hero.titleA")} <br />
            <span className="text-[#00A896]">{t("hero.titleB")}</span><br />
            {t("hero.titleC")}
          </h1>
          <p
            className="mt-8 max-w-xl text-[#C7CBD3] text-base sm:text-lg leading-relaxed fade-up"
            style={{ animationDelay: "0.2s" }}
          >
            {t("hero.subtitle")}
          </p>
          <div className="mt-10 flex flex-wrap gap-4 fade-up" style={{ animationDelay: "0.3s" }}>
            <Link to="/contact" className="btn-primary" data-testid="hero-quote-btn">
              {t("hero.quote")} <ArrowRight size={18} />
            </Link>
            <a href={`tel:${settings?.mobile || ""}`} className="btn-outline" data-testid="hero-call-btn">
              <Phone size={16} /> {settings?.mobile}
            </a>
          </div>

          <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10 fade-up" style={{ animationDelay: "0.4s" }}>
            {[
              { k: "10+", v: t("stats.years") },
              { k: "500+", v: t("stats.plants") },
              { k: "24h", v: t("stats.pickup") },
              { k: "100%", v: t("stats.pricing") },
            ].map((s) => (
              <div key={s.v} className="border-l-2 border-[#00A896] pl-4">
                <div className="font-display text-3xl lg:text-5xl font-black text-white">{s.k}</div>
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#8C939D] mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-[#282C33] py-6 overflow-hidden bg-[#0A0B0E]" data-testid="marquee-section">
        <div className="marquee-track flex gap-12 items-center">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex gap-12 items-center shrink-0">
              {["Copper", "Aluminium", "Brass", "Iron", "Stainless Steel", "Motors", "Cables", "Transformers", "CNC Machines", "Lathes", "Gearboxes", "E-Waste"].map((m) => (
                <span key={m + k} className="font-display font-black text-3xl uppercase tracking-tight text-transparent" style={{ WebkitTextStroke: "1px #00A896" }}>
                  {m} <span className="text-[#00A896]">★</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28" data-testid="services-section">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
          <div>
            <div className="overline">{t("services.overline")}</div>
            <h2 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-4 leading-none">
              {t("services.title1")}<br /><span className="text-[#00A896]">{t("services.title2")}</span>
            </h2>
          </div>
          <p className="max-w-md text-[#8C939D] text-sm leading-relaxed">{t("services.intro")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <Link
              to="/services"
              key={s.key}
              className="brut-card group block"
              data-testid={`service-card-${s.key}`}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img src={s.img} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6">
                <div className="overline text-[#8C939D]">/// 0{i + 1}</div>
                <h3 className="font-display font-bold text-2xl uppercase tracking-tight mt-3 text-white group-hover:text-[#00A896] transition-colors">{s.title}</h3>
                <p className="text-[#8C939D] mt-3 text-sm leading-relaxed">{s.desc}</p>
                <div className="mt-5 inline-flex items-center gap-2 text-[#00A896] text-sm font-mono uppercase tracking-[0.2em]">
                  {t("services.learnMore")} <ArrowUpRight size={16} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-[#14161A] border-y border-[#282C33] py-20 lg:py-28" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline">{t("why.overline")}</div>
            <h2 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl mt-4 leading-tight">
              {t("why.titleA")} <span className="text-[#00A896]">{t("why.titleB")}</span> {t("why.titleC")}
            </h2>
            <p className="text-[#8C939D] mt-6 leading-relaxed">{t("why.intro")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {benefits.map((b) => (
              <div key={b.title} className="brut-card p-6">
                <b.icon size={28} className="text-[#00A896]" />
                <div className="font-display font-bold uppercase tracking-tight text-lg mt-4 text-white">{b.title}</div>
                <p className="text-[#8C939D] text-sm mt-2 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      {gallery.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28" data-testid="home-gallery-section">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="overline">{t("home.gallery.overline")}</div>
              <h2 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl mt-4">{t("home.gallery.title")}</h2>
            </div>
            <Link to="/gallery" className="text-sm font-mono uppercase tracking-[0.2em] text-[#00A896] hidden sm:inline-flex items-center gap-2">
              {t("home.gallery.viewAll")} <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {gallery.map((g) => (
              <div key={g.id} className="brut-card aspect-square overflow-hidden">
                <img src={g.image} alt={g.title} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-[#00A896] text-[#0A0B0E] py-16 lg:py-20" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em]">{t("cta.overline")}</div>
            <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-3 leading-none">
              {t("cta.titleA")}<br />{t("cta.titleB")}
            </h2>
          </div>
          <div className="flex gap-4 flex-wrap">
            <Link to="/contact" className="bg-[#0A0B0E] text-white font-bold uppercase tracking-wider px-7 py-4 inline-flex items-center gap-2 hover:bg-[#1C1E24]" data-testid="cta-quote-btn">
              {t("cta.request")} <ArrowRight size={18} />
            </Link>
            <a href={`tel:${settings?.mobile || ""}`} className="border-2 border-[#0A0B0E] text-[#0A0B0E] font-bold uppercase tracking-wider px-7 py-4 inline-flex items-center gap-2 hover:bg-[#0A0B0E] hover:text-white">
              <Phone size={16} /> {t("nav.callNow")}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
